import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { authFetch } from "@/lib/auth";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import type { LogEntry } from "@shared/types";
const typeColors: Record<string, string> = {
  maintenance: "border-l-blue-500",
  pasture: "border-l-green-500",
  vet: "border-l-purple-500",
  "animal-care": "border-l-amber-500",
};
const typeBgColors: Record<string, string> = {
  maintenance: "bg-sky-muted/10 text-sky-muted dark:bg-sky-muted-dark/15 dark:text-sky-muted-dark",
  pasture: "bg-forest/10 text-forest dark:bg-forest-dark/15 dark:text-forest-dark",
  vet: "bg-moss/10 text-moss dark:bg-moss-dark/15 dark:text-moss-dark",
  "animal-care": "bg-burnt-orange/10 text-burnt-orange dark:bg-burnt-orange-dark/15 dark:text-burnt-orange-dark",
};
function getEntityHref(entityType: string, entityId: string): string {
  if (!entityId) return "#";
  switch (entityType) {
    case "vehicle":
      return `/vehicles/${entityId}`;
    case "animal":
      return `/animals/${entityId}`;
    case "pasture":
      return "/pastures";
    case "inventory":
      return "/inventory";
    default:
      return "#";
  }
}
export function LogsPage() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await authFetch<{ items: LogEntry[] }>("/api/logs");
        if (!cancelled) {
          const sorted = (data.items ?? []).sort((a, b) => b.date.localeCompare(a.date));
          setLogs(sorted);
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!cancelled) setError(msg);
        console.error("[Logs]", msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);
  const filtered = useMemo(() => {
    return logs.filter((l) => {
      const ms =
        l.description.toLowerCase().includes(search.toLowerCase()) ||
        l.entity_name.toLowerCase().includes(search.toLowerCase());
      const mt = typeFilter === "all" || l.log_type === typeFilter;
      return ms && mt;
    });
  }, [logs, search, typeFilter]);
  return (
    <>
      <PageHeader
        title="Activity Logs"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Logs" }]}
      />
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Log Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="pasture">Pasture</SelectItem>
            <SelectItem value="vet">Vet</SelectItem>
            <SelectItem value="animal-care">Animal Care</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <CardGridSkeleton count={6} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No logs found"
          description={
            search || typeFilter !== "all"
              ? "Try adjusting your filters."
              : "Activity logs will appear here as you record events."
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((log) => {
            const href = getEntityHref(log.entity_type, log.entity_id);
            const borderColor = typeColors[log.log_type] || "border-l-gray-400";
            const iconBg = typeBgColors[log.log_type] || "bg-surface-raised text-muted-foreground dark:bg-surface-raised-dark";
            return (
              <div
                key={log.id}
                className={cn(
                  "flex items-start gap-4 p-4 border rounded-lg bg-card border-l-4 hover:shadow-sm transition-shadow",
                  borderColor
                )}
              >
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                    iconBg
                  )}
                >
                  <ScrollText className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {href !== "#" ? (
                      <Link
                        to={href}
                        className="text-sm font-semibold text-foreground hover:text-ranch-gold transition-colors hover:underline"
                      >
                        {log.entity_name || "Unknown"}
                      </Link>
                    ) : (
                      <span className="text-sm font-semibold text-foreground">
                        {log.entity_name || "Unknown"}
                      </span>
                    )}
                    <StatusBadge status={log.log_type} />
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {log.description}
                  </p>
                  <p className="text-2xs text-muted-foreground mt-1.5">
                    {log.date
                      ? format(parseISO(log.date + "T00:00:00"), "EEEE, MMM d, yyyy")
                      : "—"}
                    {log.entity_type && (
                      <span className="ml-2 capitalize">• {log.entity_type}</span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}